import { useState } from 'react';
import { FiUpload, FiTrash2, FiMusic, FiAlertCircle } from 'react-icons/fi';
import useStore from '../store/useStore';
import { parseWavHeader } from '../services/wavParser';
import './LoopFileManager.css';

/**
 * Loop File Manager
 * Upload WAV files to 128 program slots with validation
 */

function LoopFileManager() {
    const { currentProfile, setCurrentProfile, setProfileModified, isConnected, sendProgramChange } = useStore();
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [uploadStatus, setUploadStatus] = useState(null);

    if (!currentProfile) return null;

    // WAV file validation
    const validateWAVFile = (file) => {
        const errors = [];

        // 1. File type check
        if (!file.type.includes('wav') && !file.name.toLowerCase().endsWith('.wav')) {
            errors.push('Only WAV files are supported');
        }

        // 2. File size check (max 100MB per loop, ~1.5 hours at 44.1kHz/16-bit stereo)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            errors.push(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max: 100MB`);
        }

        // 3. Minimum size check
        const minSize = 1024; // 1KB minimum
        if (file.size < minSize) {
            errors.push('File too small to be a valid WAV file');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    };

    // Advanced WAV validation (delegates to the pure, unit-tested RIFF walker)
    const validateWAVFormat = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                resolve(parseWavHeader(new DataView(e.target.result)));
            };

            reader.onerror = () => {
                resolve({ valid: false, error: 'Error reading file' });
            };

            // Read enough of the header to cover any LIST/JUNK chunks preceding "fmt ".
            reader.readAsArrayBuffer(file.slice(0, 65536));
        });
    };

    // Handle file upload
    const handleFileUpload = async (event, slot) => {
        const file = event.target.files[0];
        // Reset the input so re-selecting the SAME file fires change again.
        event.target.value = '';
        if (!file) return;

        setValidationError(null);
        setUploadStatus('Validating...');

        // Basic validation
        const basicValidation = validateWAVFile(file);
        if (!basicValidation.valid) {
            setValidationError(basicValidation.errors.join(', '));
            setUploadStatus(null);
            return;
        }

        // Advanced WAV format validation
        const formatValidation = await validateWAVFormat(file);
        if (!formatValidation.valid) {
            setValidationError(formatValidation.error);
            setUploadStatus(null);
            return;
        }

        // File is valid - update profile
        const updated = {
            ...currentProfile,
            loopSlots: {
                ...currentProfile.loopSlots,
                [slot]: {
                    fileName: file.name,
                    fileSize: file.size,
                    format: formatValidation.format,
                    uploadedAt: new Date().toISOString()
                }
            }
        };

        setCurrentProfile(updated);
        setProfileModified(true);
        setUploadStatus(`✅ ${file.name} uploaded to Slot ${slot}`);
        setSelectedSlot(null);

        setTimeout(() => setUploadStatus(null), 3000);
    };

    // Select a slot. Loading a POPULATED slot N sends Program Change N (G15).
    const handleSelectSlot = (slot) => {
        setSelectedSlot(slot);

        const hasFile = currentProfile.loopSlots && currentProfile.loopSlots[slot];
        if (hasFile && isConnected) {
            const channel = currentProfile?.midiSettings?.channel || 1;
            sendProgramChange(slot, channel);
        }
    };

    // Clear slot
    const clearSlot = (slot) => {
        const updated = {
            ...currentProfile,
            loopSlots: {
                ...currentProfile.loopSlots
            }
        };
        delete updated.loopSlots[slot];

        setCurrentProfile(updated);
        setProfileModified(true);
    };

    // Get slots in groups of 16
    const getSlotGroups = () => {
        const groups = [];
        for (let i = 0; i < 128; i += 16) {
            groups.push(Array.from({ length: 16 }, (_, j) => i + j));
        }
        return groups;
    };

    const slotGroups = getSlotGroups();

    return (
        <div className="loop-file-manager">
            <div className="manager-header">
                <h3>💾 Loop File Manager</h3>
                <p className="text-sm text-secondary">Upload WAV files to 128 program slots (0-127)</p>
            </div>

            {/* Validation Info */}
            <div className="validation-info">
                <div className="info-alert" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(255, 204, 0, 0.1)', border: '1px solid rgba(255, 204, 0, 0.3)', borderRadius: '6px', color: '#ffd700' }}>
                    <strong>⚠️ Important:</strong> This manager organizes your loop <em>slots</em>.
                    To transfer the actual audio files to the Looper, you must:
                    <ol style={{ marginLeft: '1.2rem', marginTop: '0.5rem' }}>
                        <li>Connect Looper via USB in <strong>Storage Mode</strong>.</li>
                        <li>Drag these files to the device's main folder.</li>
                        <li>Disconnect and restart Looper to index them.</li>
                    </ol>
                    <em>(MIDI connection cannot transfer audio files directly)</em>
                </div>

                <h4>✅ Supported Formats:</h4>
                <ul>
                    <li><strong>Format:</strong> WAV (uncompressed PCM only)</li>
                    <li><strong>Sample Rate:</strong> 44.1kHz or 48kHz</li>
                    <li><strong>Bit Depth:</strong> 16-bit, 24-bit, or 32-bit</li>
                    <li><strong>Channels:</strong> Mono or Stereo</li>
                    <li><strong>Max Size:</strong> 100MB per file</li>
                </ul>
            </div>

            {/* Status Messages */}
            {validationError && (
                <div className="error-message">
                    <FiAlertCircle /> {validationError}
                </div>
            )}

            {uploadStatus && (
                <div className="success-message">
                    {uploadStatus}
                </div>
            )}

            {/* Slot Grid */}
            <div className="slot-grid">
                {slotGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="slot-group">
                        <div className="group-header">Slots {group[0]}-{group[15]}</div>
                        <div className="slots">
                            {group.map(slot => {
                                const hasFile = currentProfile.loopSlots && currentProfile.loopSlots[slot];

                                return (
                                    <div
                                        key={slot}
                                        className={`slot ${hasFile ? 'has-file' : 'empty'} ${selectedSlot === slot ? 'selected' : ''}`}
                                        onClick={() => handleSelectSlot(slot)}
                                        onKeyDown={(e) => {
                                            if (e.key === ' ' || e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSelectSlot(slot);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Loop slot ${slot}${hasFile ? `, file: ${currentProfile.loopSlots[slot].fileName}` : ', empty'}. Activate to select.`}
                                    >
                                        <div className="slot-number">{slot}</div>
                                        {hasFile ? (
                                            <div className="slot-content">
                                                <FiMusic />
                                                <span className="file-name">{currentProfile.loopSlots[slot].fileName}</span>
                                                <button
                                                    className="clear-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearSlot(slot);
                                                    }}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="upload-label">
                                                <FiUpload />
                                                <span>Upload</span>
                                                <input
                                                    type="file"
                                                    accept=".wav,audio/wav,audio/x-wav"
                                                    onChange={(e) => handleFileUpload(e, slot)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </label>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default LoopFileManager;
